import { useTranslation } from 'module/hooks/use-translation';
import { ModalFuncProps } from 'antd/es/modal';
import { confirm } from 'module/ui/components/atoms/modal/methods';
import * as styles from 'module/ui/components/atoms/modal/modal.module.css';
import { Button } from 'antd';
import * as signalStyles from './use-delete-signal-confirm-modal.module.css';

type DeleteSignalConfirmConfig = ModalFuncProps & {
    isEnabled?: boolean;
    onDisable?: () => void;
};

/**
 * Signals-only delete confirmation modal.
 *
 * Intentionally separate from the shared `useDeleteConfirmModal` so Signals UI
 * changes don't affect other product areas.
 */
export const useDeleteSignalConfirmModal = (
    config: DeleteSignalConfirmConfig = {},
): (() => void) => {
    const titleText = useTranslation(
        'settings.signal_configuration.delete.confirm.title',
    );
    const messageText = useTranslation(
        'settings.signal_configuration.delete.confirm.message',
    );
    const cancelText = useTranslation('cancel');
    const disableText = useTranslation(
        'settings.signal_configuration.delete.confirm.disable_button',
    );
    const deleteText = useTranslation(
        'settings.signal_configuration.delete.confirm.delete_button',
    );

    return () => {
        const paragraphs = String(messageText)
            .split('\n\n')
            .map((p) => p.trim())
            .filter(Boolean);

        let instance:
            | ReturnType<typeof confirm>
            | undefined;

        instance = confirm({
            ...config,
            className: `${styles.modal_method} ${signalStyles.signal_delete_modal} ${config.className ?? ''}`.trim(),
            width: config.width ?? 560,
            title: config.title ?? titleText,
            okText: deleteText,
            cancelText,
            okType: 'danger',
            content: (
                <div className={signalStyles.content}>
                    {paragraphs.map((p, idx) => (
                        <div key={idx}>{p}</div>
                    ))}

                    <div className={signalStyles.footer}>
                        <div className={signalStyles.footer_left}>
                            <Button
                                className={`modal-confirm-button ${signalStyles.cancel_link}`}
                                onClick={() => {
                                    config.onCancel?.();
                                    instance?.destroy();
                                }}
                            >
                                {cancelText}
                            </Button>
                        </div>

                        <div className={signalStyles.footer_right}>
                            <Button
                                className={`modal-confirm-button ${styles.cancel_button}`}
                                onClick={() => {
                                    if (config.isEnabled === false) {
                                        instance?.destroy();
                                        return;
                                    }

                                    config.onDisable?.();
                                    instance?.destroy();
                                }}
                            >
                                {disableText}
                            </Button>
                            <Button
                                type="primary"
                                danger
                                className={`modal-confirm-button ${styles.ok_button}`}
                                onClick={() => {
                                    config.onOk?.();
                                    instance?.destroy();
                                }}
                            >
                                {deleteText}
                            </Button>
                        </div>
                    </div>
                </div>
            ),
        });
    };
};

